// Generated by GitHub Copilot
import { motion } from "framer-motion";

/**
 * Render project cards with support for static and live GitHub metadata.
 *
 * @param {{projects: Array<Record<string, any>>, config: Record<string, any>, loading?: boolean}} props Component props.
 * @returns {JSX.Element} Projects section.
 */
function Projects({ projects, config, loading = false }) {
  const featuredCaseStudies = Array.isArray(projects)
    ? projects.filter((project) => project?.featuredCaseStudy).slice(0, 3)
    : [];

  const measurableImpactCards = featuredCaseStudies.flatMap((project) => {
    const cards = Array.isArray(project?.impactCards) ? project.impactCards : [];
    return cards.map((card, cardIndex) => ({
      id: `${project.id}-impact-${cardIndex}`,
      projectTitle: project.title,
      label: card?.label || "Impact",
      value: card?.value || "Measured",
      evidence: card?.evidence || "",
    }));
  });

  const releaseRadarRows = featuredCaseStudies
    .filter((project) => project?.releaseRadar)
    .map((project) => ({
      id: project.id,
      title: project.title,
      maturityTag: project.maturityTag || "Emerging",
      latest: project.releaseRadar?.latest || "n/a",
      next: project.releaseRadar?.next || "n/a",
      cadence: project.releaseRadar?.cadence || "n/a",
      status: project.releaseRadar?.status || "Planned",
    }));

  return (
    <section className="py-16" id="projects">
      <motion.h2
        className="text-3xl font-bold"
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ amount: 0.3, once: true }}
        transition={{ duration: 0.55, ease: "easeOut" }}
      >
        {config?.title}
      </motion.h2>
      <motion.p
        className="mt-2 text-[var(--text-secondary)]"
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ amount: 0.3, once: true }}
        transition={{ duration: 0.55, delay: 0.1, ease: "easeOut" }}
      >
        {config?.subtitle}
      </motion.p>

      {featuredCaseStudies.length ? (
        <div className="mt-8 space-y-6">
          {measurableImpactCards.length ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--accent-rgb),0.95)]">
                Measurable Impact Cards
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {measurableImpactCards.map((card) => (
                  <div key={card.id} className="glass-card-soft rounded-2xl p-4">
                    <p className="text-xs uppercase tracking-[0.14em] text-[var(--text-secondary)]">{card.projectTitle}</p>
                    <p className="mt-2 text-2xl font-bold text-[rgba(var(--accent-rgb),0.95)]">{card.value}</p>
                    <p className="mt-1 text-sm font-semibold text-[var(--text-primary)]">{card.label}</p>
                    {card.evidence ? (
                      <p className="mt-2 text-sm text-[var(--text-secondary)]">{card.evidence}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--accent-rgb),0.95)]">
              Top 3 Case Studies
            </p>
            <div className="mt-3 grid gap-4 lg:grid-cols-3">
              {featuredCaseStudies.map((project, index) => (
                <motion.article
                  key={`${project.id}-featured`}
                  className="glass-card rounded-3xl p-5"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ amount: 0.2, once: true }}
                  transition={{ duration: 0.45, delay: index * 0.08, ease: "easeOut" }}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-[0.14em] text-[var(--text-secondary)]">
                      Case Study {index + 1}
                    </p>
                    {project.maturityTag ? (
                      <span className="glass-chip rounded-full px-3 py-1 text-xs text-[rgba(var(--accent-rgb),0.95)]">
                        {project.maturityTag}
                      </span>
                    ) : null}
                  </div>
                  <h3 className="mt-2 text-lg font-semibold text-[var(--text-primary)]">{project.title}</h3>
                  <p className="mt-2 text-sm text-[var(--text-secondary)]">{project.description}</p>
                  {project.situation ? (
                    <p className="mt-3 text-sm text-[var(--text-secondary)]">
                      <span className="font-semibold text-[var(--text-primary)]">Situation:</span> {project.situation}
                    </p>
                  ) : null}
                  {project.task ? (
                    <p className="mt-2 text-sm text-[var(--text-secondary)]">
                      <span className="font-semibold text-[var(--text-primary)]">Task:</span> {project.task}
                    </p>
                  ) : null}
                  {Array.isArray(project.actions) && project.actions.length ? (
                    <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-[var(--text-secondary)]">
                      {project.actions.slice(0, 2).map((actionItem, actionIndex) => (
                        <li key={`${project.id}-featured-action-${actionIndex}`}>{actionItem}</li>
                      ))}
                    </ul>
                  ) : null}
                  {project.result ? (
                    <p className="mt-3 text-sm text-[var(--text-secondary)]">
                      <span className="font-semibold text-[var(--text-primary)]">Result:</span> {project.result}
                    </p>
                  ) : null}
                </motion.article>
              ))}
            </div>
          </div>

          {releaseRadarRows.length ? (
            <div className="glass-card rounded-3xl p-6 md:p-8">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--accent-rgb),0.95)]">
                  Release Radar
                </p>
                <p className="text-xs text-[var(--text-secondary)]">Maturity tags and next release targets</p>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-[0.14em] text-[var(--text-secondary)]">
                      <th className="pb-3 pr-4 font-semibold">Project</th>
                      <th className="pb-3 pr-4 font-semibold">Maturity</th>
                      <th className="pb-3 pr-4 font-semibold">Latest</th>
                      <th className="pb-3 pr-4 font-semibold">Next</th>
                      <th className="pb-3 pr-4 font-semibold">Cadence</th>
                      <th className="pb-3 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {releaseRadarRows.map((row) => (
                      <tr key={`${row.id}-release`} className="border-t border-[color:color-mix(in_srgb,var(--glass-border)_58%,transparent_42%)]">
                        <td className="py-3 pr-4 font-semibold text-[var(--text-primary)]">{row.title}</td>
                        <td className="py-3 pr-4">
                          <span className="glass-chip rounded-full px-3 py-1 text-xs text-[rgba(var(--accent-rgb),0.95)]">
                            {row.maturityTag}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-[var(--text-secondary)]">{row.latest}</td>
                        <td className="py-3 pr-4 text-[var(--text-secondary)]">{row.next}</td>
                        <td className="py-3 pr-4 text-[var(--text-secondary)]">{row.cadence}</td>
                        <td className="py-3 text-[var(--text-secondary)]">{row.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-8">
        {loading ? (
          <div
            className="space-y-6"
            role="status"
            aria-live="polite"
            aria-label="Loading project cards"
            aria-busy="true"
          >
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={`project-skeleton-${index}`} className="glass-card rounded-3xl p-6 md:p-8">
                <div className="grid gap-3">
                  <div className="skeleton skeleton-text--lg" style={{ width: "60%" }} />
                  <div className="skeleton skeleton-text" style={{ width: "35%" }} />
                  <div className="skeleton skeleton-text" style={{ width: "96%" }} />
                  <div className="skeleton skeleton-text" style={{ width: "88%" }} />
                  <div className="skeleton h-24 rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {projects.map((project, index) => {
              const tags = project.tags?.length ? project.tags : ["Engineering"];
              const tools = Array.isArray(project.tools) ? project.tools : [];
              const actions = Array.isArray(project.actions) ? project.actions : [];
              const isGithubProject = project.source === "github";
              const projectLink = project.github || project.link;
              const projectLinkLabel = project.github ? "View on GitHub" : project.linkLabel || "Open project";

              return (
                <motion.article
                  key={project.id}
                  className="glass-card rounded-3xl p-6 transition hover:-translate-y-1 md:p-8"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ amount: 0.2, once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07, ease: "easeOut" }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-2xl font-semibold">
                        {isGithubProject && projectLink ? (
                          <a
                            href={projectLink}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline"
                            aria-label={`Open ${project.title} on GitHub`}
                          >
                            {project.title}
                          </a>
                        ) : (
                          project.title
                        )}
                      </h3>
                      {project.organization || project.period ? (
                        <p className="mt-2 text-xs uppercase tracking-[0.14em] text-[var(--text-secondary)]">
                          {[project.organization, project.period].filter(Boolean).join(" | ")}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {isGithubProject ? (
                        projectLink ? (
                          <a
                            href={projectLink}
                            target="_blank"
                            rel="noreferrer"
                            className="glass-chip rounded-full px-3 py-1 text-xs text-[rgba(var(--accent-rgb),0.95)] hover:underline"
                            aria-label={`Open ${project.title} repository`}
                          >
                            GitHub Project
                          </a>
                        ) : (
                          <span className="glass-chip rounded-full px-3 py-1 text-xs text-[rgba(var(--accent-rgb),0.95)]">
                            GitHub Project
                          </span>
                        )
                      ) : null}
                      {tags.map((tag) => (
                        <span
                          key={`${project.id}-${tag}`}
                          className="glass-chip rounded-full px-3 py-1 text-xs text-[var(--text-primary)]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <p className="mt-4 text-[var(--text-secondary)]">{project.description}</p>

                  {project.imageUrl ? (
                    <img
                      src={project.imageUrl}
                      alt={`${project.title} preview`}
                      loading="lazy"
                      className="mt-4 h-56 w-full rounded-2xl object-cover"
                    />
                  ) : null}

                  {project.situation ? (
                    <div className="mt-4 rounded-2xl bg-[color-mix(in_srgb,var(--glass-surface)_58%,transparent_42%)] p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[rgba(var(--accent-rgb),0.95)]">Situation</p>
                      <p className="mt-2 text-sm text-[var(--text-secondary)]">{project.situation}</p>
                    </div>
                  ) : null}

                  {project.task ? (
                    <div className="mt-3 rounded-2xl bg-[color-mix(in_srgb,var(--glass-surface)_58%,transparent_42%)] p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[rgba(var(--accent-rgb),0.95)]">Task</p>
                      <p className="mt-2 text-sm text-[var(--text-secondary)]">{project.task}</p>
                    </div>
                  ) : null}

                  {actions.length ? (
                    <div className="mt-3 rounded-2xl bg-[color-mix(in_srgb,var(--glass-surface)_58%,transparent_42%)] p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[rgba(var(--accent-rgb),0.95)]">Action</p>
                      <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm text-[var(--text-secondary)]">
                        {actions.map((actionItem, actionIndex) => (
                          <li key={`${project.id}-action-${actionIndex}`}>{actionItem}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {project.result ? (
                    <div className="mt-3 rounded-2xl bg-[color-mix(in_srgb,var(--glass-surface)_58%,transparent_42%)] p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[rgba(var(--accent-rgb),0.95)]">Result</p>
                      <p className="mt-2 text-sm text-[var(--text-secondary)]">{project.result}</p>
                    </div>
                  ) : null}

                  {project.currentWork ? (
                    <p className="mt-3 text-sm text-[var(--text-secondary)]">
                      <span className="font-semibold text-[var(--text-primary)]">Current Work:</span> {project.currentWork}
                    </p>
                  ) : null}

                  {tools.length ? (
                    <p className="mt-3 text-sm text-[var(--text-secondary)]">
                      <span className="font-semibold text-[var(--text-primary)]">Tools:</span> {tools.join(", ")}
                    </p>
                  ) : null}

                  {isGithubProject ? (
                    <p className="mt-2 text-xs text-[var(--text-secondary)]">
                      Generated from GitHub repository metadata and activity signals.
                    </p>
                  ) : null}

                  {projectLink ? (
                    <a
                      className="mt-5 inline-block text-sm font-semibold text-[rgba(var(--accent-rgb),0.95)] hover:underline"
                      href={projectLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {projectLinkLabel}
                    </a>
                  ) : null}
                </motion.article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export default Projects;
